<template>
	<BottomSheet
		title="Shop"
		v-model="openProductDetail"
		:height="'90%'"
		:close-icon="'chevron'"
		@close="onClose"
		:loading="loading"
	>
		<div class="product-detail" v-if="productDetail">
			<div class="product-detail__image flex-center mb-4">
				<img :src="getImage" />
			</div>
			<h5 class="product-detail__title">{{ productDetail.name }}</h5>
			<p class="product-detail__label product-detail__price">
				<span>
					{{
						selectedVariant.promotionPrice > 0
							? selectedVariant.promotionPrice.toLocaleString("en-US")
							: selectedVariant.price.toLocaleString("en-US")
					}}
					{{ currency }}
				</span>
				<span
					v-if="selectedVariant.promotionPrice > 0"
					class="product-detail__label product-detail__label--grey product-detail__price--discount"
				>
					{{ selectedVariant.price.toLocaleString("en-US") }} {{ currency }}
				</span>
			</p>
			<div
				v-for="(variantOption, index) in productDetail.options"
				:key="`variantOption-${index}`"
				class="product-detail__size mb-3"
			>
				<p class="product-detail__label">{{ variantOption.name }}</p>
				<VariantSelect
					v-model="selectedOptions[`option${index + 1}`]"
					:data="variantOption.values.map((value) => ({ label: value, value }))"
				/>
			</div>
			<div class="product-detail__quantity mb-3">
				<p class="product-detail__label">Quantity</p>
				<div class="product-detail__input-quantity flex-center">
					<button
						:disabled="loading"
						class="btn-icon btn-minus product-detail__btn-quantity"
						@click="if (quantity > 1) quantity--;"
					></button>
					<input v-model="quantity" min="0" type="number" />
					<button
						:disabled="loading"
						class="btn-icon btn-plus product-detail__btn-quantity"
						@click="quantity++"
					></button>
				</div>
			</div>
			<div
				v-if="productDetail.description"
				class="product-detail__description mb-3"
			>
				<p class="product-detail__label">Description</p>
				<p class="product-detail__label product-detail__label--grey">
					{{ productDetail.description }}
				</p>
			</div>
		</div>
		<template #action>
			<div class="product-detail__action">
				<button
					:disabled="loading"
					class="btn btn-primary flex-center product-detail__btn-add-to-cart"
					@click="addToCart"
				>
					<span class="loading product-detail__loading" v-if="loading"></span>
					<span v-else>ADD TO CART</span>
				</button>
			</div>
		</template>
	</BottomSheet>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from "vue";
import BottomSheet from "../shared/BottomSheet.vue";
import VariantSelect from "./VariantSelect.vue";
import { useCartIntegration } from "@/vue/composables/useCartIntegration";
import { useToast } from "~/vue/composables/useToast";
import { computed } from "@vue/reactivity";

const {
	openProductDetail,
	openProductList,
	productDetail,
	loading,
	addToCart: addItemToCart,
	currency,
} = useCartIntegration();
const { toast } = useToast();

const quantity = ref(1);

const selectedOptions = reactive({
	option1: null,
	option2: null,
	option3: null,
});

const selectedVariant = computed(() => {
	const variant = getVariantBySelectedOptions(
		productDetail.value.variants || [],
		Object.values(selectedOptions).filter((option) => !!option)
	);
	return variant || { images: [], promotionPrice: 0, price: 0 };
});

const getImage = computed(() => {
	return selectedVariant.value.images[0] || productDetail.value.image;
});

function getVariantBySelectedOptions(variants, selected) {
	return variants.find(
		(variant) =>
			variant.options.length === selected.length &&
			variant.options.every((option) => selected.includes(option))
	);
}

function setDefaultOption(product) {
	if (!product.options?.length) return;
	product.options.forEach((option, index) => {
		selectedOptions[`option${index + 1}`] = option.values[0];
	});
}

watch(productDetail, (val) => {
	if (!val) return;
	setDefaultOption(val);
});

onMounted(() => {
	if (!openProductDetail.value || !productDetail.value) return;
	setDefaultOption(productDetail.value);
});

watch(openProductDetail, (val) => {
	if (!val) {
		quantity.value = 1;
		for (const key in selectedOptions) {
			selectedOptions[key] = null;
		}
	}
});

async function addToCart() {
	const { isSuccess, error } = await addItemToCart(
		selectedVariant.value,
		quantity.value
	);
	if (isSuccess) {
		toast.success("Item added successfully!");
	} else {
		toast.error(
			error?.description || error?.message || "Error on adding item to cart!"
		);
	}
}

function onClose() {
	if (!openProductList.value) {
		openProductList.value = true;
	}
}
</script>
